import { z } from 'zod';

// =============================================================================
// DESKTOP BRIDGE — `window.wayaiDesktop`, exposed by the WayAI desktop shell (apps/desktop)
//
// The shell loads the hosted web app as a remote page. Its preload exposes this object to
// the page only when the top-level document is on the build's app origin, and relays each
// call over IPC to the main process. BOTH sides validate with the schemas below: the main
// process treats every call as untrusted (sender frame and payload), and the web treats
// every answer as untrusted (an old or foreign shell).
//
// VERSIONING. `bridgeVersion` is the shape of the whole object. The web never requires a
// shell newer than the one installed: it reads `bridgeVersion` and feature-detects each
// call, because a web deploy reaches every installed shell at once while shells update on
// their own schedule. So an answer may only GROW — a new optional field, never a renamed,
// removed or retyped one — and a call is never removed or given a narrower argument list
// without a new `bridgeVersion`.
// =============================================================================

/**
 * The version of the bridge this contract describes. 2 added `getPresence` and `onPresence`;
 * 3 added `openSignIn`; 4 added `getUpdate`, `onUpdate` and `restartToUpdate`; 5 added `notify`,
 * `setBadge`, `clearAll` and `onNavigate`; 6 added `getSettings` and `setSettings`, and a
 * notification's `withoutPreview`.
 */
export const DESKTOP_BRIDGE_VERSION = 6;

/** The global the preload defines on the app origin: `window.wayaiDesktop`. */
export const DESKTOP_BRIDGE_GLOBAL = 'wayaiDesktop';

/** The IPC channel of each bridge call, between the shell's preload and main process. */
export const DESKTOP_BRIDGE_CHANNELS = {
  getShellInfo: 'wayai-desktop:get-shell-info',
  getPresence: 'wayai-desktop:get-presence',
  openSignIn: 'wayai-desktop:open-sign-in',
  getUpdate: 'wayai-desktop:get-update',
  restartToUpdate: 'wayai-desktop:restart-to-update',
  notify: 'wayai-desktop:notify',
  setBadge: 'wayai-desktop:set-badge',
  clearAll: 'wayai-desktop:clear-all',
  getSettings: 'wayai-desktop:get-settings',
  setSettings: 'wayai-desktop:set-settings',
} as const;

/** The IPC channel of each event the main process pushes to the preload. */
export const DESKTOP_BRIDGE_EVENTS = {
  /** The new `DesktopPresence`, each time the person's presence or the window's state changes. */
  presence: 'wayai-desktop:presence',
  /** The new `DesktopUpdate`, each time it changes (a shell update becomes ready, or its restart is offered). */
  update: 'wayai-desktop:update',
  /** A `DesktopNavigate`: the person clicked a notification, and the page opens its path. */
  navigate: 'wayai-desktop:navigate',
} as const;

/** A shell release: MAJOR.MINOR.PATCH with an optional prerelease tag (`0.2.0-beta.1`). */
export const DESKTOP_SHELL_VERSION_RE =
  /^(0|[1-9]\d{0,5})\.(0|[1-9]\d{0,5})\.(0|[1-9]\d{0,5})(-[0-9A-Za-z.-]{1,32})?$/;

/** The operating systems the shell is built for. */
export const DESKTOP_PLATFORMS = ['darwin', 'win32'] as const;

/**
 * Whether a notification on `platform` may carry a customer's name and words: only where the shell
 * can remove them at sign-out. macOS removes every notification the app delivered, an earlier run's
 * included; Windows gives an app no way to remove an earlier run's (see `clearAll`).
 */
export function desktopShowsCustomerWords(platform: string): boolean {
  return platform === 'darwin';
}

/** A build flavor: production loads app.wayai.pro, development a local dev host. */
export const DESKTOP_FLAVORS = ['production', 'development'] as const;
export type DesktopFlavor = (typeof DESKTOP_FLAVORS)[number];

/** `getShellInfo()`'s answer: which shell build is hosting the page. */
export const desktopShellInfoSchema = z.object({
  /** The shell's own release version, independent of the web deploy. */
  shellVersion: z.string().regex(DESKTOP_SHELL_VERSION_RE),
  /** The Electron runtime the shell was built with. */
  electronVersion: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}(-[0-9A-Za-z.-]{1,32})?$/),
  platform: z.enum(DESKTOP_PLATFORMS),
  /** CPU architecture as Node reports it (`arm64`, `x64`). */
  arch: z.string().regex(/^[a-z0-9_]{1,16}$/),
  flavor: z.enum(DESKTOP_FLAVORS),
  /**
   * Present, and true, when the shell runs from the Microsoft Store's package: the Store updates it,
   * and Windows alone opens it at login (the package's startup task, which the person turns on in
   * Windows' settings). An optional field, so no new `bridgeVersion`: an older page ignores it, and
   * its absence is every other shell.
   */
  store: z.literal(true).optional(),
});
export type DesktopShellInfo = z.infer<typeof desktopShellInfoSchema>;

/** Whether the person is at the computer, as the operating system tells the shell. */
export const DESKTOP_PRESENCE_STATES = ['present', 'away'] as const;

/**
 * `getPresence()`'s answer and each `onPresence` event: the person's presence (away while the
 * screen is locked, the machine asleep, or no OS input for the shell's idle threshold), and the
 * shell's main window (`shown`: neither hidden nor minimized).
 */
export const desktopPresenceSchema = z.object({
  presence: z.enum(DESKTOP_PRESENCE_STATES),
  mainWindow: z.object({
    shown: z.boolean(),
    focused: z.boolean(),
  }),
});
export type DesktopPresence = z.infer<typeof desktopPresenceSchema>;

/**
 * `getUpdate()`'s answer and each `onUpdate` event: the shell's own update (Electron, the bridge,
 * the tray — never the web app, which updates with every web deploy).
 *
 * `ready` is a newer shell release, downloaded and verified, that installs when the app quits;
 * null while there is none. The page's sidebar update icon offers a restart for it. `offerRestart`
 * says whether the shell's tray offers one too: the shell sets it with `ready`, except that the first
 * v4 shells set it only once the update had waited a day — so the page reads `ready`, not this.
 */
export const desktopUpdateSchema = z.object({
  ready: z
    .object({
      version: z.string().regex(DESKTOP_SHELL_VERSION_RE),
      offerRestart: z.boolean(),
    })
    .nullable(),
});
export type DesktopUpdate = z.infer<typeof desktopUpdateSchema>;

/** The longest authorize URL `openSignIn` takes; WorkOS's is a few hundred characters. */
export const DESKTOP_SIGN_IN_URL_MAX_LENGTH = 4096;

/**
 * How long an `openSignIn` attempt can be finished: the lifetime of the web's PKCE verifier cookie
 * (`PKCE_COOKIE_MAX_AGE` in apps/web, pinned equal by its test), past which the callback could not
 * exchange the code anyway.
 */
export const DESKTOP_SIGN_IN_MAX_AGE_SECONDS = 600;

/** The longest app path a notification opens. */
export const DESKTOP_APP_PATH_MAX_LENGTH = 512;

/**
 * A path on the app origin (`/support/<conversation id>`), with an optional query: never a URL, a
 * protocol-relative `//host`, a backslash or whitespace, so it can only ever name a page of the app.
 */
export const desktopAppPathSchema = z
  .string()
  .max(DESKTOP_APP_PATH_MAX_LENGTH)
  .regex(/^\/(?![/\\])[^\s\\]*$/);

/**
 * What a notification announces: a conversation handed to the person's team, a customer's message
 * in a conversation they claimed, or a summary of several (the queue waiting after a reconnect, or
 * a replay's arrivals collapsed into one). The shell treats a `message` differently — its title and
 * body may be a customer's name and words (`withoutPreview`).
 */
export const DESKTOP_NOTIFICATION_KINDS = ['handoff', 'message', 'summary'] as const;

export const DESKTOP_NOTIFICATION_TITLE_MAX_LENGTH = 200;
export const DESKTOP_NOTIFICATION_BODY_MAX_LENGTH = 500;

const notificationTitleSchema = z.string().min(1).max(DESKTOP_NOTIFICATION_TITLE_MAX_LENGTH);
const notificationBodySchema = z.string().max(DESKTOP_NOTIFICATION_BODY_MAX_LENGTH);

/**
 * `notify()`'s argument: one notification, its text already in the person's language, and the app
 * path a click on it opens (`onNavigate`).
 *
 * `withoutPreview` (bridge 6), read for a `message` only: the same announcement without the
 * customer's name or words. A shell from bridge 6 shows it instead of `title` and `body` unless its
 * platform shows a customer's words (`desktopShowsCustomerWords`) and the person's `messagePreview`
 * setting is on; an older shell drops the field and shows `title` and `body`, as does a newer one
 * for a `message` sent without it.
 */
export const desktopNotificationSchema = z.object({
  kind: z.enum(DESKTOP_NOTIFICATION_KINDS),
  title: notificationTitleSchema,
  body: notificationBodySchema,
  path: desktopAppPathSchema,
  withoutPreview: z.object({ title: notificationTitleSchema, body: notificationBodySchema }).optional(),
});
export type DesktopNotification = z.infer<typeof desktopNotificationSchema>;

/** The largest queue count `setBadge` takes; far past anything a badge can show. */
export const DESKTOP_BADGE_MAX_COUNT = 1_000_000;
export const DESKTOP_BADGE_LABEL_MAX_LENGTH = 200;

/**
 * `setBadge()`'s argument: the count of the person's team queue the web's support badge shows (0
 * clears it), and the line that says it in their language, for the tray and the taskbar.
 */
export const desktopBadgeSchema = z.object({
  count: z.number().int().min(0).max(DESKTOP_BADGE_MAX_COUNT),
  label: z.string().max(DESKTOP_BADGE_LABEL_MAX_LENGTH),
});
export type DesktopBadge = z.infer<typeof desktopBadgeSchema>;

/** Each `onNavigate` event: the path of the notification the person clicked. */
export const desktopNavigateSchema = z.object({
  path: desktopAppPathSchema,
});
export type DesktopNavigate = z.infer<typeof desktopNavigateSchema>;

/**
 * `getSettings()`'s answer, and `setSettings()`'s: the shell's preferences on this device (bridge 6).
 * They never reach a server, and each device keeps its own.
 */
export const desktopSettingsSchema = z.object({
  /** The app opens when the person logs in to the computer: the operating system's own login item. */
  openAtLogin: z.boolean(),
  /** Closing the window keeps the app running in the tray (true), or quits it (false). */
  closeToTray: z.boolean(),
  /** The shell shows notifications (true), or none (false). The badge is not a notification. */
  notifications: z.boolean(),
  /**
   * A customer message's notification shows the customer's name and words, where the platform
   * allows them at all (`desktopShowsCustomerWords`: macOS); elsewhere the shell never shows them.
   */
  messagePreview: z.boolean(),
});
export type DesktopSettings = z.infer<typeof desktopSettingsSchema>;

/**
 * `setSettings()`'s argument: the settings to change, each true or false (a setting named with no
 * value is refused, not read as a change); the others keep their value.
 */
export const desktopSettingsChangeSchema = desktopSettingsSchema
  .partial()
  .strict()
  .refine((change) => Object.values(change).every((value) => typeof value === 'boolean'), {
    message: 'each setting changed is true or false',
  });
export type DesktopSettingsChange = z.infer<typeof desktopSettingsChangeSchema>;

/**
 * Each call's argument list as the main process receives it. A call that takes nothing takes
 * an EMPTY list: extra arguments are refused rather than ignored.
 */
export const desktopBridgeArgsSchemas = {
  getShellInfo: z.tuple([]),
  getPresence: z.tuple([]),
  openSignIn: z.tuple([z.string().max(DESKTOP_SIGN_IN_URL_MAX_LENGTH)]),
  getUpdate: z.tuple([]),
  restartToUpdate: z.tuple([]),
  notify: z.tuple([desktopNotificationSchema]),
  setBadge: z.tuple([desktopBadgeSchema]),
  clearAll: z.tuple([]),
  getSettings: z.tuple([]),
  setSettings: z.tuple([desktopSettingsChangeSchema]),
} as const satisfies Record<keyof typeof DESKTOP_BRIDGE_CHANNELS, z.ZodTypeAny>;

/** `window.wayaiDesktop` at `bridgeVersion` 1. */
export interface DesktopBridgeV1 {
  readonly bridgeVersion: number;
  getShellInfo(): Promise<DesktopShellInfo>;
}

/** `window.wayaiDesktop` at `bridgeVersion` 2. */
export interface DesktopBridgeV2 extends DesktopBridgeV1 {
  getPresence(): Promise<DesktopPresence>;
  /**
   * Calls `listener` with the new state each time the presence or the window's state changes,
   * until the returned function is called.
   */
  onPresence(listener: (state: DesktopPresence) => void): () => void;
}

/** `window.wayaiDesktop` at `bridgeVersion` 3. */
export interface DesktopBridgeV3 extends DesktopBridgeV2 {
  /**
   * Social sign-in in the system browser. Takes the WorkOS authorize URL the web's sign-in route
   * answered for the desktop (`/api/auth/sign-in?client=desktop`). The shell opens it in the
   * system browser only when it is WorkOS's authorize endpoint for this build's client and
   * redirect, and records it as the one sign-in in progress; the browser's redirect back to the
   * shell then finishes it in the window, through the web's own callback. Rejects when the URL is
   * refused or the browser could not be opened.
   */
  openSignIn(authorizationUrl: string): Promise<void>;
}

/** `window.wayaiDesktop` at `bridgeVersion` 4. */
export interface DesktopBridgeV4 extends DesktopBridgeV3 {
  getUpdate(): Promise<DesktopUpdate>;
  /** Calls `listener` with the new state each time it changes, until the returned function is called. */
  onUpdate(listener: (state: DesktopUpdate) => void): () => void;
  /**
   * Quits the shell, installs the ready update and starts the new version. Rejects when no update
   * is ready.
   */
  restartToUpdate(): Promise<void>;
}

/** `window.wayaiDesktop` at `bridgeVersion` 5. */
export interface DesktopBridgeV5 extends DesktopBridgeV4 {
  /**
   * Shows `notification` as the operating system's own. A click shows and focuses the window and
   * sends its path to `onNavigate`. Rejects when the notification is refused.
   */
  notify(notification: DesktopNotification): Promise<void>;
  /** Puts the team-queue count on the dock or taskbar icon and in the tray; a count of 0 clears it. */
  setBadge(badge: DesktopBadge): Promise<void>;
  /**
   * For sign-out: removes the notifications the shell delivered — on macOS every one, an earlier
   * run's included; on Windows the ones this run still holds, as Windows gives an app no way to
   * remove what an earlier run delivered (so the page never puts a customer's name or words in a
   * Windows notification) — and clears the badge and the tray count.
   */
  clearAll(): Promise<void>;
  /** Calls `listener` with each clicked notification's path, until the returned function is called. */
  onNavigate(listener: (target: DesktopNavigate) => void): () => void;
}

/** `window.wayaiDesktop` at `bridgeVersion` 6. */
export interface DesktopBridgeV6 extends DesktopBridgeV5 {
  /** The shell's settings on this device, as they stand. */
  getSettings(): Promise<DesktopSettings>;
  /**
   * Changes the settings `change` names and answers them all as they now stand — read back from the
   * operating system for `openAtLogin`, which it may refuse. Rejects when the change is refused or
   * could not be saved; nothing changed then.
   */
  setSettings(change: DesktopSettingsChange): Promise<DesktopSettings>;
}
